import React from 'react'
import { Alert, Button, Card, Checkbox, Flex, Select, Tag, Typography } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../types/company/Company'
import StepHeading from './StepHeading'
import FieldLabel from './FieldLabel'

const MONO_FONT = 'ui-monospace, Menlo, monospace'

interface WizardStepCompanyProps {
  companies: Company[]
  groupCompanyId?: string
  targetId: string
  selectedSiblingIds: Set<string>
  onSelectTarget: (id: string) => void
  onToggleSibling: (id: string) => void
  onCancel: () => void
  onSearch: () => void
}

const WizardStepCompany: React.FC<WizardStepCompanyProps> = ({
  companies,
  groupCompanyId,
  targetId,
  selectedSiblingIds,
  onSelectTarget,
  onToggleSibling,
  onCancel,
  onSearch
}) => {
  const { t } = useTranslation()
  const target = companies.find(company => company.id === targetId)
  const siblings = companies.filter(company => company.id !== targetId)
  const selectedCount = selectedSiblingIds.size

  return (
    <>
      <StepHeading
        title={t('groupCustomerMapping:wizardCompanyTitle', 'Mikä yhtiö linkitetään?')}
        description={t(
          'groupCustomerMapping:wizardCompanyDescription',
          'Valitse konserniin liitetty yhtiö. Sisaryhtiöistä valitaan oletuksena ne, joilta linkitys puuttuu — jo linkitetyt jäävät valitsematta.'
        )}
      />

      <Flex gap={16} style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel>{t('groupCustomerMapping:wizardCompanyCompany', 'Yhtiö')}</FieldLabel>
          <Select
            style={{ width: '100%' }}
            value={targetId || undefined}
            placeholder={t('groupCustomerMapping:wizardSelectCompany', 'Valitse yhtiö…')}
            onChange={onSelectTarget}
            options={companies.map(company => ({ value: company.id, label: company.name }))}
            aria-label={t('groupCustomerMapping:wizardCompanyCompany', 'Yhtiö')}
          />
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>{t('groupCustomerMapping:wizardCompanyBusinessId', 'Y-tunnus · lähdejärjestelmä')}</FieldLabel>
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: '4px 11px',
              color: '#444',
              fontFamily: MONO_FONT,
              fontSize: 13
            }}
          >
            {target ? `${target.id} · ${target.accountingSoftware}` : '—'}
          </div>
        </div>
      </Flex>

      {target && (
        <>
          <FieldLabel size={11} mb={7}>
            {t(
              'groupCustomerMapping:wizardCompanySiblingsHeader',
              'Linkitettävät sisaryhtiöt · {{selected}} / {{total}} valittu — oletuksena kaikki ilman linkitystä',
              { selected: selectedCount, total: siblings.length }
            )}
          </FieldLabel>

          <Card size="small" style={{ marginBottom: 14 }} styles={{ body: { padding: 0 } }}>
            {siblings.map(sibling => (
              <Flex
                key={sibling.id}
                align="center"
                gap={10}
                style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0' }}
              >
                <Checkbox
                  checked={selectedSiblingIds.has(sibling.id)}
                  onChange={() => onToggleSibling(sibling.id)}
                  aria-label={t('groupCustomerMapping:wizardCompanySiblingSelect', 'Valitse sisaryhtiö {{name}}', {
                    name: sibling.name
                  })}
                />
                <Typography.Text style={{ flex: 1, fontSize: 14 }}>{sibling.name}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: MONO_FONT }}>
                  {sibling.id}
                </Typography.Text>
                <Tag>
                  {sibling.id === groupCompanyId
                    ? t('groupCustomerMapping:wizardCompanyParent', 'emoyhtiö')
                    : t('groupCustomerMapping:wizardCompanySubsidiary', 'tytäryhtiö')}
                </Tag>
              </Flex>
            ))}
          </Card>

          <Alert
            type="info"
            description={t(
              'groupCustomerMapping:wizardCompanyRecordCount',
              'Linkityksiä syntyy {{target}}lle valittujen sisaryhtiöiden mukaan. Mitään ei kirjata ennen vaihetta 4.',
              { target: target.name }
            )}
            style={{ marginBottom: 8 }}
          />
        </>
      )}

      <Flex justify="space-between">
        <Button onClick={onCancel}>{t('groupCustomerMapping:wizardCompanyCancel', 'Peruuta')}</Button>
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          iconPosition="end"
          onClick={onSearch}
          disabled={!targetId || selectedCount === 0}
        >
          {t('groupCustomerMapping:wizardCompanySearch', 'Etsi vastineet')}
        </Button>
      </Flex>
    </>
  )
}

export default WizardStepCompany
